import { z } from "zod";
import { capabilitySchema } from "./capabilities";
import { hospitalLogoSchema } from "./hospitals";
import { booleanString, dateSchema, patientGenderSchema } from "./utils";

export const patientCreateBodySchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  gender: patientGenderSchema,
  phone: z.e164(),
  birthdate: dateSchema,
});

export const patientSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  gender: patientGenderSchema,
  phone: z.e164(),
  // coerce: the contract must parse on BOTH ends — the server validates Date
  // objects on the way out, clients parse ISO strings on the way in (see
  // StudyResponseSchema, which already does this).
  birthdate: z.coerce.date(),
  createdAt: z.coerce.date(),
  lastLoginAt: z.coerce.date().nullable(),
  isArchived: z.boolean(),
  /**
   * Server-computed conclusions about what THE CALLER may do to THIS patient.
   * Global today (the matrix has no per-patient scope yet), but the wire
   * shape is per-resource on purpose: the department/panel pass will scope it
   * and no client changes. Optional because the mobile self-read extends this
   * schema and a patient has no staff permissions to report.
   */
  permissions: z.partialRecord(capabilitySchema, z.boolean()).optional(),
});

export type CreatePatientInput = z.infer<typeof patientCreateBodySchema>;
export type PatientResponse = z.infer<typeof patientSchema>;

/**
 * What a patient reads about THEMSELVES (`GET /api/mobile/patient/:id`), as
 * opposed to what a clinician reads about a cohort.
 *
 * The facility rides along here rather than on `patientSchema` for two
 * reasons: it is the same row for every patient in a single-hospital
 * deployment, so a paginated cohort list would repeat it once per row for
 * nothing, and the only screen that needs it is the patient's own app. It is
 * NULLABLE because the settings row is created by the seed, not by a
 * migration — a deployment that has not been bootstrapped, and every test
 * database that does not stand one up, has no facility to name. Clients drop
 * the line rather than inventing one.
 */
export const patientProfileSchema = patientSchema.extend({
  hospital: z
    .object({
      name: z.string(),
      /**
       * The facility's own logo when it has uploaded one, so the app a patient
       * lands in from an SMS looks like their hospital's rather than like a
       * vendor's. Null is the ordinary case for a fresh deployment: the client
       * falls back to the name above.
       */
      logo: hospitalLogoSchema.nullable(),
    })
    .nullable(),
});
export type PatientProfileResponse = z.infer<typeof patientProfileSchema>;

export const patientUpdateParamsSchema = z.object({ id: z.string() });

export const patientUpdateBodySchema = patientCreateBodySchema.partial();
export type UpdatePatientInput = z.infer<typeof patientUpdateBodySchema>;

export const patientArchiveParamsSchema = z.object({ id: z.string() });
export const patientArchiveBodySchema = z.object({ isArchived: z.boolean() });
export type ArchivePatientInput = z.infer<typeof patientArchiveBodySchema>;

export const getPatientsRequestParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().optional(),
  /** Defaults to active patients only; pass true for the archive. */
  isArchived: booleanString.optional(),
  /**
   * Free-text search over first name, last name and phone. Whitespace splits
   * it into terms that must ALL match somewhere, so "marie dup" finds Marie
   * Dupont. A term that looks like a phone number is matched on its digits,
   * ignoring separators and the national prefix.
   */
  search: z.string().trim().min(1).max(100).optional(),
});
export const getPatientsResponseSchema = z.object({
  patients: z.array(patientSchema),
  totalPages: z.number(),
  currentPage: z.number(),
});
export type GetPatientsRequest = z.infer<typeof getPatientsRequestParamsSchema>;
export type GetPatientsResponse = z.infer<typeof getPatientsResponseSchema>;
